import * as Sentry from '@sentry/nextjs';

import { ApiResponse } from '@/lib/models/ApiResponse';

export interface HttpOptions {
  params?: Record<string, string>;
  signal?: AbortSignal;
  next?: NextFetchRequestConfig | undefined;
  headers?: HeadersInit;
}

export interface HttpResponse<T = unknown> {
  status: number;
  data: ApiResponse<T>;
}

export function getUrlWithParams(path: string, options: HttpOptions): string {
  const url = new URL(path.startsWith('/') ? `${process.env.NEXT_PUBLIC_API_URL}${path}` : path);
  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }
  return url.toString();
}

export function getRequestInit(options: HttpOptions, init: Partial<RequestInit> = {}): RequestInit {
  return {
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'include',
    next: options.next,
    signal: options.signal,
    ...init,
  };
}

export async function fetchData<R>(url: string, init: RequestInit): Promise<HttpResponse<R>> {
  try {
    const response = await fetch(url, init);
    const json = (await response.json()) as ApiResponse<R>;

    if (!response.ok) {
      const errorMessage = json?.error?.message || 'Unknown error occurred';
      const error = new Error(errorMessage);
      error.name = `HTTP Error ${response.status}`;

      Sentry.captureException(error, {
        tags: { url },
        extra: { init, responseStatus: response.status, responseBody: json },
      });
      throw error;
    }

    return {
      status: response.status,
      data: json,
    };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { url },
      extra: { init },
    });
    throw error;
  }
}

export async function get<R>(path: string, options: HttpOptions = {}): Promise<HttpResponse<R>> {
  const url = getUrlWithParams(path, options);
  const init = getRequestInit(options);

  return fetchData<R>(url, init);
}

export async function post<R, B = unknown>(path: string, body: B, options: HttpOptions = {}): Promise<HttpResponse<R>> {
  const url = getUrlWithParams(path, options);
  const init = getRequestInit(options, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  return fetchData<R>(url, init);
}
